import {Injectable} from '@angular/core';
import {IUser} from './auth.types';
import {HttpService} from './http.service';
import {BehaviorSubject, Observable} from 'rxjs';
import {RouterUtilityService} from './router.utility.service';
import {Location} from '@angular/common';
import {PUBLIC_CONTEXT} from '../../environments/environment.const';

@Injectable()
export class AuthService {
    public user: IUser;
    defaultUserLoggedInUrl: string = null;
    singleUseToken = new BehaviorSubject<string>(undefined);
    isLoggedIn = new BehaviorSubject<boolean>(false);
    isRedirectToCreateAccount = new BehaviorSubject<boolean>(false);
    isLoginDialogOpen = new BehaviorSubject<boolean>(false);
    CONTEXT;

    public isUserAuthenticated = false;
    public isPublicUser = false;
    public loggedInUser: IUser;
    public passwordPattern = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{6,}$/;

    constructor(private http: HttpService,
                private routerUtilityService: RouterUtilityService,
                private location: Location) {
    }

    clearAllCredentials() {
        localStorage.removeItem('session');
        this.isUserAuthenticated = false;
        this.isPublicUser = false;
        this.user = undefined;
        this.loggedInUser = undefined;
        return true;
    }

    get token() {
        return localStorage.getItem('session');
    }

    logout(): Observable<any> {
        return this.http.get('api/logout', {observe: 'response'});
    }

    authenticate() {
        return new Promise((resolve, reject) => {
            const singleUseToken = this.singleUseToken.value;
            if (!singleUseToken) {
                if (!localStorage.getItem('session')) {
                    if (this.facilitatedPublicUser()) {
                        resolve(true);
                    }
                } else {
                    this.authenticateAndGetInitials(`api/authenticate?is_initialization=true`, resolve, reject, false);
                }
            } else {
                if (localStorage.getItem('session')) {
                    this.authenticateAndGetInitials(`api/contexts`, resolve, reject, false);
                } else {
                    localStorage.removeItem('session');
                    this.authenticateAndGetInitials(`api/authenticate?single_use_token=${singleUseToken}`, resolve, reject, true);
                }
            }
        }) as Promise<any>;
    }

    private facilitatedPublicUser(): boolean {
        this.isUserAuthenticated = true;
        this.isPublicUser = true;
        this.CONTEXT = PUBLIC_CONTEXT;
        return true;
    }

    isRequestEditable(requestId: number, flowData: any, isPendingTaskFileUploadAllowed?: boolean): boolean {

        let result = flowData.tender_revision_status_slug !== 'added-to-npc-meeting-agenda' &&
            flowData.tender_revision_status_slug !== 'published' &&
            flowData.tender_revision_status_slug !== 'rejected' &&
            !flowData.is_request_published_with_pending_tasks;

        if (isPendingTaskFileUploadAllowed === false) {

            result = result && (
                flowData.tender_revision_status_slug === 'published-with-pending-tasks' ||
                flowData.tender_revision_status_slug === 'pe-modified-for-pending-tasks' ||
                flowData.tender_revision_status_slug === 'pe-submitted-for-pending-tasks' ||
                flowData.tender_revision_status_slug === 'pe-reviewed-for-pending-tasks' ||
                flowData.tender_revision_status_slug === 'pe-approved-for-pending-tasks' ||
                flowData.tender_revision_status_slug === 'npcs-submitted-for-pending-tasks' ||
                flowData.tender_revision_status_slug === 'npcs-reviewed-for-pending-tasks' ||
                flowData.tender_revision_status_slug === 'npcs-approved-for-pending-tasks');
        }
        return result;
    }

    authenticateAndGetInitials(url: string, resolve, reject, canSetSession) {
        this.isPublicUser = false;
        this.http.get(url).subscribe((data) => {
            if (canSetSession) {
                localStorage.setItem('session', JSON.stringify(data.session));
            }
            this.isUserAuthenticated = true;
            if (url === 'api/contexts') {
                this.CONTEXT = data && data.contexts;
                this.loggedInUser = data && data.logged_in_user;
                this.loggedInUser.v_pin = data && data.user && data.user.v_pin;
                this.isLoggedIn.next(true);
                resolve(true);
            } else {
                this.http.get('api/contexts').subscribe((userData) => {
                    this.CONTEXT = userData && userData.contexts;
                    this.loggedInUser = userData && userData.logged_in_user;
                    this.loggedInUser.v_pin = data && data.user && data.user.v_pin;
                    this.isLoggedIn.next(true);
                    resolve(true);
                });
            }
        }, (error) => {
            if (error.status === 401) {
                document.getElementsByTagName('iframe')
                    .item(0).contentWindow.postMessage(JSON.stringify({isLoggedOut: true}), '*');
            }
            if (this.facilitatedPublicUser()) {
                localStorage.removeItem('session');
                resolve(true);
            } else {
                localStorage.removeItem('session');
                this.isUserAuthenticated = false;
                this.routerUtilityService.navigateIfUnauthorized();
                reject(true);
            }
        });
    }

    // can(identifier: string, type: string, value: string , options?: ICanOptions[], context?) {
    //     context = this.CONTEXT || context;
    //     let tenderId;
    //     let processingBody;
    //     let awardingBody;
    //     let procurementEntitySlug;
    //     options.forEach((item) => {
    //         if (item.type === 'tenderId') {
    //             tenderId = item.value;
    //         } else if (item.type === 'processingBody') {
    //             processingBody = item.value;
    //         } else if (item.type === 'awardingBody') {
    //             awardingBody = item.value;
    //         } else if (item.type === 'procurementEntitySlug') {
    //             procurementEntitySlug = item.value;
    //         }
    //     });
    //     tenderId = tenderId ? tenderId : this.itemService.info.getValue().data.tender_id;
    //     processingBody = processingBody ? processingBody : this.itemService.info.getValue().data.processing_body;
    //     awardingBody = awardingBody ? awardingBody : this.itemService.info.getValue().data.awarding_body;
    //     procurementEntitySlug = procurementEntitySlug ? procurementEntitySlug : this.itemService.info.getValue().data.procurement_entity_slug;
    //     if (type === 'context_operation') {
    //         if (this.isPublicUser || this.loggedInUser.party_slug === 'vendor') {
    //             const contextOperationIndex =
    //                 context.findIndex(item => item.identifier === identifier && item.type === type && item.identifier_value === value);
    //             if (contextOperationIndex >= 0) {
    //                 return true;
    //             }
    //         } else if (this.loggedInUser.party_slug === 'default') {
    //             if (this.loggedInUser.sub_party_slug === 'npa') {
    //                 const contextOperationIndex =
    //                     context.findIndex(item => item.identifier === identifier && item.type === type && item.identifier_value === value);
    //                 if (contextOperationIndex >= 0 && (this.loggedInUser.sub_party_slug === processingBody ||
    //                     this.loggedInUser.sub_party_slug === awardingBody)) {
    //                     return true;
    //                 }
    //             } else if (this.loggedInUser.sub_party_slug !== 'pe') {
    //                 const contextOperationIndex =
    //                     context.findIndex(item => item.identifier === identifier && item.type === type && item.identifier_value === value);
    //                 if (contextOperationIndex >= 0 &&
    //                     this.loggedInUser.sub_party_slug === procurementEntitySlug &&
    //                     (this.loggedInUser.sub_party_slug === processingBody ||
    //                         this.loggedInUser.sub_party_slug === awardingBody)) {
    //                     return true;
    //                 }
    //             }
    //
    //         } else {
    //             const contextOperationIndex = context.findIndex(item => item.identifier === identifier && item.type === type &&
    //                 item.identifier_value === value && item.tender_id === tenderId);
    //             if (contextOperationIndex >= 0) {
    //                 return true;
    //             }
    //         }
    //     }
    //
    //     return false;
    // }

    public extractPath(path: string) {
        if (path.includes('?')) {
            path = path.split('?')[0];
        }
        if (path.includes('#')) {
            path = path.split('#')[0];
        }
        return path;
    }
}

